/**
 * Prompt Editor
 * Monospace textarea with variable highlighting for editing prompt content.
 */

import { useMemo } from 'react';

interface PromptEditorProps {
  content: string;
  onChange: (content: string) => void;
  variables?: string[];
  readOnly?: boolean;
}

export default function PromptEditor({
  content,
  onChange,
  readOnly = false,
}: PromptEditorProps) {
  // Extract variables used in the template
  const usedVariables = useMemo(() => {
    const matches = content.match(/\{\{(\w+)\}\}/g) || [];
    return [...new Set(matches.map((m) => m.slice(2, -2)))];
  }, [content]);

  return (
    <div className="space-y-2">
      {/* Variable badges */}
      {usedVariables.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {usedVariables.map((v) => (
            <span
              key={v}
              className="inline-block px-2 py-0.5 text-xs font-mono border-2 border-blue-500 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20"
            >
              {`{{${v}}}`}
            </span>
          ))}
        </div>
      )}

      {/* Editor */}
      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={`w-full h-96 p-4 font-mono text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white resize-y focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:focus:shadow-[4px_4px_0px_0px_rgba(55,65,81,1)] transition-shadow ${
          readOnly ? 'opacity-70 cursor-not-allowed' : ''
        }`}
        spellCheck={false}
      />

      {/* Char/token count */}
      <div className="flex justify-end text-xs text-gray-500 dark:text-gray-400 font-mono">
        {content.length} chars / ~{Math.ceil(content.length / 4)} tokens
      </div>
    </div>
  );
}
